import { all, put, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import localSettings from "@app/config/LocalSettings";
import CoachingFile from "@app/scene/dashboard/types/CoachingFile";
import CoachingNote, { CoachingNoteAttachmentChip } from "@app/scene/dashboard/types/CoachingNote";
import { toAttachmentChip } from "@app/scene/dashboard/utils/coachingFileResolvers";
import { getCoachingTaskActivityType } from "@app/store/activity";
import { fetchCoachingTaskActivityType } from "@app/store/activity/actions";
import { callApi, callReadonlyApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";

import { uploadCoachingFiles } from "../files/actions";

import {
  createCoachingNote,
  createReply,
  deleteCoachingNote,
  fetchCoachingNotes,
  fetchReplies,
  updateCoachingNote,
} from "./actions";

const DEFAULT_LIMIT = 25;
const TASKS_PAGE_SIZE = 1000;

function* ensureCoachingTaskTypeLoaded() {
  let taskType: ActivityType | undefined = yield select(getCoachingTaskActivityType);
  if (taskType) {
    return taskType;
  }
  // Block notes load until the lazily-fetched type resolves so the first-page
  // linked-task co-fetch isn't skipped on the initial Coaching-tab visit.
  yield put(fetchCoachingTaskActivityType.request());
  yield race({
    failure: take(fetchCoachingTaskActivityType.failure),
    success: take(fetchCoachingTaskActivityType.success),
  });
  taskType = yield select(getCoachingTaskActivityType);
  return taskType;
}

function* onFetchCoachingNotes({ payload }: ReturnType<typeof fetchCoachingNotes.request>) {
  // Anonymous mode is a read-only demo view: writes no-op server-side, so the
  // underlying data can't change. A silent live-poll would only re-fetch stale
  // server truth and resurrect notes the user optimistically created or deleted
  // this session (e.g. a deleted note reappearing). Skip it; the non-silent
  // initial / filtered loads still populate the feed.
  if (payload.silent && localSettings.isAnonymousFlagRaised()) {
    return;
  }
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const coachingTaskType: ActivityType | undefined = yield* ensureCoachingTaskTypeLoaded();
    const append = payload.append ?? false;
    const limit = payload.limit ?? DEFAULT_LIMIT;
    const offset = payload.offset ?? 0;

    const $filters: Record<string, unknown> = { userId: payload.subjectUserId };
    if (isNotEmpty(payload.categoryId)) {
      $filters.categoryId = payload.categoryId;
    }
    if (payload.visibility) {
      $filters.visibility = payload.visibility;
    }
    if (payload.parentNoteId !== undefined) {
      $filters.parentNoteId = payload.parentNoteId;
    }
    if (payload.q) {
      $filters.q = payload.q;
    }

    const notesResponse: ListResponse<CoachingNote> = yield callReadonlyApi(
      "listCoachingNotes",
      orgId,
      { $filters, $limit: limit, $offset: offset, $order: "-updatedAt" }
    );

    const taskTypeId = coachingTaskType?.id;
    // Co-fetch tasks only on the first page; append-page tasks are already in state.
    const noteIds = notesResponse.data.map((note) => note.id);
    const shouldCoFetchTasks = isNotEmpty(taskTypeId) && !append && noteIds.length > 0;
    let tasks: Activity[] | undefined;
    if (shouldCoFetchTasks) {
      const tasksFilters: Record<string, unknown> = {
        coachingNoteId: { $in: noteIds },
        crmActivityTypeId: { $eq: taskTypeId },
        includeCoachingTasks: true,
        subjectUserId: { $eq: payload.subjectUserId },
      };
      const firstPage: ListResponse<Activity> = yield callReadonlyApi("fetchActivities", orgId, {
        $filters: tasksFilters,
        $limit: TASKS_PAGE_SIZE,
      });
      const fetched: Activity[] = [...firstPage.data];
      const apiCalls = [];
      if (firstPage.total > TASKS_PAGE_SIZE) {
        for (let i = 1; i < Math.ceil(firstPage.total / TASKS_PAGE_SIZE); i += 1) {
          apiCalls.push(
            callReadonlyApi("fetchActivities", orgId, {
              $filters: tasksFilters,
              $limit: TASKS_PAGE_SIZE,
              $offset: TASKS_PAGE_SIZE * i,
            })
          );
        }
      }
      const remainingPages: ListResponse<Activity>[] = yield all(apiCalls);
      remainingPages.forEach((page) => {
        fetched.push(...page.data);
      });
      tasks = fetched;
    }

    const tasksByNote = new Map<CoachingNote["id"], Activity["id"][]>();
    if (tasks) {
      for (const task of tasks) {
        if (!isNotEmpty(task.coachingNoteId)) {
          continue;
        }
        const list = tasksByNote.get(task.coachingNoteId) ?? [];
        list.push(task.id);
        tasksByNote.set(task.coachingNoteId, list);
      }
    }

    const hydratedNotes: CoachingNote[] = tasks
      ? notesResponse.data.map((note) => ({
          ...note,
          linkedTaskIds: tasksByNote.get(note.id) ?? [],
        }))
      : notesResponse.data;

    yield put(
      fetchCoachingNotes.success({
        append,
        hasMore: notesResponse.offset + notesResponse.data.length < notesResponse.total,
        notes: hydratedNotes,
        offset: notesResponse.offset,
        silent: !!payload.silent,
      })
    );
  } catch (error) {
    // A silent poll failure stays invisible — skip the error state + toast so a
    // transient hiccup doesn't disrupt the user; the next poll retries.
    if (!payload.silent) {
      yield put(fetchCoachingNotes.failure(error));
      yield put(handleError({ error }));
    }
  }
}

function* onCreateCoachingNote({ payload }: ReturnType<typeof createCoachingNote.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // Same orchestration pattern as createCoachingTask: dropped-in files are
    // uploaded to the rep's pool first, then their CoachingFile ids merged
    // with the picker selections so the new note's coachingNoteAttachments
    // junction includes them. Without this, dropped files only ended up in
    // the pool and the new note showed no chip.
    let uploadedAttachments: CoachingFile[] = [];
    if (payload.newFiles && payload.newFiles.length > 0) {
      uploadedAttachments = yield all(
        payload.newFiles.map((file) =>
          callApi("createCoachingFile", orgId, file, payload.subjectUserId)
        )
      );
      yield put(uploadCoachingFiles.success(uploadedAttachments));
    }
    const combinedCoachingFileIds = [
      ...(payload.coachingFileIds ?? []),
      ...uploadedAttachments.map((cf) => cf.id),
    ];
    const created: CoachingNote = yield callApi("createCoachingNote", orgId, {
      answers: payload.answers.map(({ promptId, value }) => ({ promptId, value })),
      categoryId: payload.categoryId ?? null,
      coachingFileIds: combinedCoachingFileIds.length > 0 ? combinedCoachingFileIds : undefined,
      note: payload.note,
      subjectUserId: payload.subjectUserId,
      visibility: payload.visibility,
    });
    // Server doesn't expose the user relation or `attachments` on create —
    // graft both onto the success note so the feed can render authorName +
    // chips without waiting for the next list refetch. The temp note was
    // seeded with only the pool-selected chips at request time; freshly
    // uploaded CoachingFiles need converting to the chip shape.
    const grafted: CoachingNoteAttachmentChip[] = [
      ...(payload.attachments ?? []),
      ...uploadedAttachments.map(toAttachmentChip),
    ];
    const note: CoachingNote = {
      ...created,
      ...(grafted.length > 0 ? { attachments: grafted } : {}),
      mentions: created.mentions ?? payload.mentions,
      user: created.user ?? payload.tempAuthor,
    };
    yield put(createCoachingNote.success({ note, tempId: payload.tempId }));
  } catch (error) {
    yield put(createCoachingNote.failure({ tempId: payload.tempId }));
    yield put(handleError({ error }));
  }
}

function* onUpdateCoachingNote({ payload }: ReturnType<typeof updateCoachingNote.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // Mirror the create path's atomic upload→write: dropped-in files go to
    // the rep's pool first, then their CoachingFile ids merge with the
    // picker selections in coachingFileIds on the PUT.
    let uploadedAttachments: CoachingFile[] = [];
    if (payload.newFiles && payload.newFiles.length > 0) {
      uploadedAttachments = yield all(
        payload.newFiles.map((file) =>
          callApi("createCoachingFile", orgId, file, payload.subjectUserId)
        )
      );
      yield put(uploadCoachingFiles.success(uploadedAttachments));
    }
    const combinedCoachingFileIds =
      payload.coachingFileIds !== undefined || uploadedAttachments.length > 0
        ? [...(payload.coachingFileIds ?? []), ...uploadedAttachments.map((cf) => cf.id)]
        : undefined;
    // Detach-only: the PUT's coachingFileIds reconciliation removes the
    // coachingNoteAttachments link via the BE's _syncAttachments without
    // touching the rep's pool. Calling deleteCoachingFile here would wipe
    // the file from the pool entirely — only the explicit pool-delete X
    // in the Files modal should do that.
    const updated: CoachingNote = yield callApi("updateCoachingNote", orgId, payload.id, {
      answers: payload.answers?.map(({ promptId, value }) => ({ promptId, value })),
      categoryId: payload.categoryId,
      coachingFileIds: combinedCoachingFileIds,
      note: payload.note,
      visibility: payload.visibility,
    });
    // BE update response doesn't include `attachments` (only list/fetch run
    // _hydrateAttachments). Graft the request's pre-shaped chips plus chips
    // for any freshly-uploaded files so the chip list reflects the new
    // selection without a refetch.
    const graftedAttachments: CoachingNoteAttachmentChip[] | undefined =
      payload.attachments !== undefined || uploadedAttachments.length > 0
        ? [...(payload.attachments ?? []), ...uploadedAttachments.map(toAttachmentChip)]
        : undefined;
    const updatedNote: CoachingNote = {
      ...updated,
      ...(graftedAttachments !== undefined ? { attachments: graftedAttachments } : {}),
      user: updated.user ?? payload.user,
    };
    yield put(updateCoachingNote.success(updatedNote));
  } catch (error) {
    yield put(updateCoachingNote.failure(payload.id));
    yield put(handleError({ error }));
  }
}

function* onDeleteCoachingNote({ payload }: ReturnType<typeof deleteCoachingNote.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteCoachingNote", orgId, payload.noteId);
    yield put(
      deleteCoachingNote.success({ noteId: payload.noteId, parentNoteId: payload.parentNoteId })
    );
  } catch (error) {
    yield put(deleteCoachingNote.failure(payload.noteId));
    yield put(handleError({ error }));
  }
}

function* onFetchReplies({ payload }: ReturnType<typeof fetchReplies.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const append = payload.append ?? false;
    const offset = payload.offset ?? 0;
    const limit = payload.limit ?? DEFAULT_LIMIT;
    const $filters: Record<string, unknown> = { parentNoteId: payload.parentNoteId };
    const response: ListResponse<CoachingNote> = yield callReadonlyApi("listCoachingNotes", orgId, {
      $filters,
      $limit: limit,
      $offset: offset,
      $order: "createdAt",
    });
    yield put(
      fetchReplies.success({
        append,
        // Force false on empty pages — IntersectionObserver auto-fires when the
        // sentinel stays visible, so a stale total > offset would loop forever.
        hasMore:
          response.data.length > 0 && response.offset + response.data.length < response.total,
        offset: response.offset,
        parentNoteId: payload.parentNoteId,
        replies: response.data,
        silent: !!payload.silent,
      })
    );
  } catch (error) {
    // Silent reply polls fail invisibly — surfacing an error would pop the
    // "Couldn't load more replies" banner and (via pollReplies' error guard)
    // stop further polling for a thread the user is still watching.
    if (!payload.silent) {
      yield put(fetchReplies.failure({ error, parentNoteId: payload.parentNoteId }));
      yield put(handleError({ error }));
    }
  }
}

function* onCreateReply({ payload }: ReturnType<typeof createReply.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // Same upload→link orchestration as onCreateCoachingNote: dropped-in files
    // go to the rep's pool first, then their CoachingFile ids merge with the
    // picker selections so the reply's coachingNoteAttachments junction covers
    // both. Replies POST to the same endpoint, and the BE's _syncAttachments
    // isn't gated on parentNoteId, so no reply-specific contract is needed.
    let uploadedAttachments: CoachingFile[] = [];
    if (payload.newFiles && payload.newFiles.length > 0) {
      uploadedAttachments = yield all(
        payload.newFiles.map((file) =>
          callApi("createCoachingFile", orgId, file, payload.subjectUserId)
        )
      );
      yield put(uploadCoachingFiles.success(uploadedAttachments));
    }
    const combinedCoachingFileIds = [
      ...(payload.coachingFileIds ?? []),
      ...uploadedAttachments.map(({ id }) => id),
    ];
    // Server forces visibility + categoryId to match parent's; we only send what
    // the contract actually consumes for replies.
    const created: CoachingNote = yield callApi("createCoachingNote", orgId, {
      coachingFileIds: combinedCoachingFileIds.length > 0 ? combinedCoachingFileIds : undefined,
      note: payload.note,
      parentNoteId: payload.parentNoteId,
      subjectUserId: payload.subjectUserId,
    });
    // Create doesn't return `attachments` (only list/fetch hydrate them), so
    // graft the request's pool chips plus chips for the freshly uploaded files
    // onto the success reply — same as the note create path.
    const grafted: CoachingNoteAttachmentChip[] = [
      ...(payload.attachments ?? []),
      ...uploadedAttachments.map(toAttachmentChip),
    ];
    const note: CoachingNote = {
      ...created,
      ...(grafted.length > 0 ? { attachments: grafted } : {}),
      mentions: created.mentions ?? payload.mentions,
      user: created.user ?? payload.tempAuthor,
    };
    yield put(createReply.success({ note, tempId: payload.tempId }));
  } catch (error) {
    yield put(createReply.failure({ parentNoteId: payload.parentNoteId, tempId: payload.tempId }));
    yield put(handleError({ error }));
  }
}

export function* coachingNotesSagas() {
  yield takeLatest(fetchCoachingNotes.request, onFetchCoachingNotes);
  yield takeEvery(createCoachingNote.request, onCreateCoachingNote);
  yield takeEvery(updateCoachingNote.request, onUpdateCoachingNote);
  yield takeEvery(deleteCoachingNote.request, onDeleteCoachingNote);
  yield takeEvery(fetchReplies.request, onFetchReplies);
  yield takeEvery(createReply.request, onCreateReply);
}
